import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ERROR_SOUND from '@assets/audio/windows_xp_error.mp3';

import CommonImage from '@/components/CommonImage';
import InputField from '@/components/InputField';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastWarning } from '@/components/Toast';
import { getFormattedQRcode, getScanType, getViewTypeFromUserPreference } from '@/features/Scan/utils';
import { SCAN_TYPE } from '@/features/Scan/constants';
import {
	useLazyFetchScannedItemByQRCodeQuery,
	useLazyFetchProductTagQuery,
	useUpdateProductLocationWithQRCodeMutation,
	useUndoLocationUpdateMutation,
	useLazySearchLocationQuery,
	useReplaceLocationMutation,
	useLikeProductMutation
} from '@/features/Scan/api/scanApi';
import { useDownloadTpSummaryMutation } from '@/api/commonDownloadsApi';
import {
	resetScanStore,
	setRowQrCode,
	setScannedItems,
	setScannedSampleIds,
	setSearchValue,
	setSelectedProductIds,
	setIsCycleStart,
	setIsLocationScannedBefore,
	setLocationResponseFromLocationScan,
	resetScannedItems,
	setLocationUpdateResponse
} from '@/features/Scan/redux/scanSlice';
import SelectFolderPopup from '@/features/favourites/components/SelectFolderPopup';
import UpdateSampleLocation from '@/features/favourites/components/UpdateSampleLocation';
import TagUpdateModal from '@/features/collection/details/TagUpdateModal';
import Footer from '@/features/Scan/components/Footer';
import ScannedStyles from '@/features/Scan/components/ScannedStyles';
import AddTagBottomPopup from '@/features/Scan/components/AddTagBottomPopup';
import ScanWithCamera from '@/features/Scan/components/ScanWithCamera';
import { useCommonTags } from '@/hooks/useCommonTags';
import { useLocationScroll } from '@/features/Scan/hooks/useLocationScroll';
import { getUserPreference, getLoadingState, getErrorMessage } from '@/utils/Util';
import AddTaskModal from '@/components/AddTaskModal';
// import CreateNewTask from '@/features/Scan/components/CreateNewTask';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal';
import SearchTypeDropdown from '@/features/Scan/components/SearchTypeDropdown';

const errorSound = new Audio(ERROR_SOUND);

const Scan = ({ title = 'Scan', onClose }) => {
	const [viewType, setViewType] = useState('');
	const [searchType, setSearchType] = useState('QR_CODE');
	const [isShowMore, setIsShowMore] = useState(false);
	const [isShowFavouriteFolders, setIsShowFavouriteFolders] = useState(false);
	const [selectedSingleSample, setSelectedSingleSample] = useState(null);
	const [isShowUpdateLocationModal, setIsShowUpdateLocationModal] = useState(false);
	const [isCreateNewFolder, setIsCreateNewFolder] = useState(false);
	const [isShowTagModal, setIsShowTagModal] = useState(false);
	const [isScanWithCamera, setIsScanWithCamera] = useState(false);
	const [capturedImageSrc, setCapturedImageSrc] = useState(null);
	const [displayedErrors, setDisplayedErrors] = useState([]);
	const [page, setPage] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [isShowTaskModal, setIsShowTaskModal] = useState(false);
	// const [isShowAddNewTaskModal, setIsShowAddNewTaskModal] = useState(false);
	const userPreference = getUserPreference();
	const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
	// const [taskInputValueFromTaskModal, setTaskInputValueFromTaskModal] = useState('');

	const searchInputRef = useRef(null);
	const scanAgainRef = useRef(null);
	const scanContainerRef = useRef(null);

	const scannedLocationRef = useRef(null);

	const dispatch = useDispatch();
	const {
		locationUpdateResponse,
		scannedItems,
		searchValue,
		scannedSampleIds,
		selectedProductIds,
		isCycleStart,
		locationResponseFromLocationScan,
		isLocationScannedBefore
	} = useSelector((state) => state.scanStore);

	// API Hooks
	const [fetchScannedItemByQRCode, { isLoading: isSearchLoading }] = useLazyFetchScannedItemByQRCodeQuery();
	const [downloadTpSummary, { isLoading: isDownloadTpSummaryLoading, error: downloadTpSummaryError }] =
		useDownloadTpSummaryMutation();
	const [
		fetchProductTag,
		{ data: productWiseTags, isLoading: isFetchingProductTag, error: fetchingProductTagError }
	] = useLazyFetchProductTagQuery();
	const [updateProductLocationWithQRCode, { isLoading: isUpdatingLocation }] =
		useUpdateProductLocationWithQRCodeMutation();
	const [undoLocationUpdate, { isLoading: isUndoingLocationUpdate, error: undoLocationUpdateError }] =
		useUndoLocationUpdateMutation();
	const [searchLocation, { isLoading: isSearchingLocation }] = useLazySearchLocationQuery();
	const [replaceLocation, { isLoading: isReplacingLocation }] = useReplaceLocationMutation();
	const [likeProduct] = useLikeProductMutation();
	const [scannedQrCode, setScannedQrCode] = useState(null);
	const [currentLikedStyle, setCurrentLikedStyle] = useState(null);

	const commonTags = useCommonTags(scannedItems, selectedProductIds);

	useLocationScroll({
		containerRef: scanContainerRef,
		hasMore,
		onLoadMore: async () => {
			return searchLocation({
				locationIdentifierNo: scannedLocationRef.current,
				page: page + 1
			})
				.unwrap()
				.then((response) => {
					const hasMorePages =
						response.productResponsePage.currentPage < response.productResponsePage.totalPages;
					setHasMore(hasMorePages);
					setPage((prev) => prev + 1);
				})
				.catch(() => {
					setHasMore(false);
				});
		}
	});

	useEffect(() => {
		setViewType(getViewTypeFromUserPreference());
	}, []);

	useEffect(() => {
		if (window.innerWidth < 768) {
			handleOpenMobileCamera();
		}
		return () => {
			setIsScanWithCamera(false);
		};
	}, [window.innerWidth]);

	// Auto-search when using camera scanner
	useEffect(() => {
		if (searchValue && isScanWithCamera) {
			handleSearch();
		}
	}, [searchValue, isScanWithCamera]);

	useEffect(() => {
		if (searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [searchType, searchValue]);

	// Handles multiple search types
	const handleSearch = () => {
		dispatch(setRowQrCode(searchValue));
		const formattedQRcode = getFormattedQRcode(searchValue);

		if (Object.keys(formattedQRcode)?.length === 0) {
			dispatch(setSearchValue(''));
			toastError('Wrong sample ID/Location ID format');
			errorSound.play();

			return;
		}

		if (isDuplicateSampleScan(formattedQRcode)) {
			return;
		}

		const scanType = getScanType(formattedQRcode, isCycleStart, isLocationScannedBefore);
		handleScanByType(scanType, formattedQRcode);
	};

	const isDuplicateSampleScan = (formattedQRcode) => {
		const isDuplicate = scannedItems.some(
			(item) => item.sampleInventoryItemResponseList?.[0]?.sampleId === formattedQRcode.sampleId
		);

		if (isDuplicate) {
			dispatch(setSearchValue(''));
			errorSound.play();
			toastWarning('Sample already scanned');
		}

		return isDuplicate;
	};

	const handleScanByType = (scanType, qrCode) => {
		setScannedQrCode(qrCode);
		switch (scanType) {
			// Fetch Sample Details when QR code is scanned and no location is scanned before and no cycle start
			case SCAN_TYPE.NEW_SAMPLE_SCAN:
				handleNewSampleScan(qrCode);
				break;
			// When locatin is Scanned and cycle started and location is not scanned before, then update the location
			case SCAN_TYPE.UPDATE_LOCATION:
				handleLocationUpdate(qrCode);
				break;
			// When Location is Scanned and there is no cycle start and location is not scanned before then fetch all the sample in that location
			case SCAN_TYPE.FETCH_LOCATION_SAMPLES:
				handleFetchLocationSamples(qrCode);
				break;
			// When Location is Scanned and cycle started and location is scanned before then replace the location
			case SCAN_TYPE.REPLACE_LOCATION:
				setIsShowConfirmationModal(true);
				break;
			default:
				break;
		}
	};

	const handleNewSampleScan = (qrCode) => {
		// Scan styles => scan Location => scan styles again => reset scanned items || Location Scan > Scan Style -> reset
		if (
			(scannedItems.length > 0 && !isCycleStart) ||
			(isLocationScannedBefore && scannedItems.length > 0)
		) {
			dispatch(resetScannedItems());
			dispatch(setLocationResponseFromLocationScan({}));
			dispatch(setLocationUpdateResponse({}));
		}

		setHasMore(false);
		if (Object.keys(locationResponseFromLocationScan).length > 0) {
			dispatch(setLocationResponseFromLocationScan({}));
		}
		dispatch(setIsCycleStart(true));
		dispatch(setIsLocationScannedBefore(false));

		fetchScannedItemByQRCode(qrCode.sampleId);
	};

	const handleLocationUpdate = (qrCode) => {
		dispatch(setIsCycleStart(false));
		updateProductLocationWithQRCode({
			sampleIds: scannedSampleIds?.filter(Boolean),
			locationIdentifierNo: qrCode.locationId
		});
	};

	const handleFetchLocationSamples = (qrCode) => {
		setPage(0);
		setHasMore(true);
		window.scrollTo(0, 0);
		dispatch(resetScannedItems());
		scannedLocationRef.current = qrCode.locationId;
		dispatch(setLocationUpdateResponse({}));
		searchLocation({
			locationIdentifierNo: qrCode.locationId,
			page: 0
		})
			.unwrap()
			.then((response) => {
				const hasMorePages =
					response.productResponsePage.currentPage < response.productResponsePage.totalPages;
				setHasMore(hasMorePages);
				if (response?.productResponsePage?.data?.length > 0) {
					dispatch(setIsCycleStart(true));
					dispatch(setIsLocationScannedBefore(true));
				} else {
					dispatch(setIsCycleStart(false));
					dispatch(setIsLocationScannedBefore(false));
				}
			})
			.catch(() => {
				setHasMore(false);
			});
	};

	const handleReplaceLocation = (qrCode) => {
		dispatch(setIsCycleStart(false));
		dispatch(setIsLocationScannedBefore(false));
		replaceLocation({
			newLocationIdentifierNo: qrCode.locationId,
			currentLocationId: locationResponseFromLocationScan?.locationResponse?.id
		})
			.unwrap()
			.then(() => {
				setIsShowConfirmationModal(false);
				setScannedQrCode(null);
			});
	};

	const handleClearAll = () => {
		dispatch(resetScanStore());
	};

	const handleRemoveItem = (sampleId, productId) => {
		dispatch(
			setScannedItems(
				scannedItems.filter((item) => item?.sampleInventoryItemResponseList?.[0]?.id !== sampleId)
			)
		);
		dispatch(setSelectedProductIds(selectedProductIds.filter((item) => item !== productId)));
		dispatch(setScannedSampleIds(scannedSampleIds.filter((item) => item !== sampleId)));
	};

	const updateProductTagOnSelect = (payload = []) => {
		const updatedProducts = scannedItems?.map((product) => {
			const matchingPayload = payload.find((item) => item.id === product?.id);
			return matchingPayload
				? { ...product, tagResponseList: matchingPayload.tagResponseList }
				: product;
		});
		dispatch(setScannedItems(updatedProducts));
	};

	const fetchAllTags = (search) => {
		fetchProductTag({
			params: {
				type: 'PRODUCT_COLLECTION_TAGS',
				entityActivityType: 'PRODUCT_TAG_UPDATE',
				search
			}
		});
	};

	const handleUndoLocationUpdate = () => {
		if (Object.keys(locationResponseFromLocationScan).length > 0) {
			dispatch(
				setLocationResponseFromLocationScan({
					locationResponse: {
						...locationResponseFromLocationScan?.locationResponse,
						count: locationUpdateResponse?.count
					}
				})
			);
		}
		dispatch(setIsCycleStart(true));
		dispatch(setIsLocationScannedBefore(Object.keys(locationResponseFromLocationScan).length > 0));
		undoLocationUpdate({ sampleIds: scannedSampleIds });
		scannedLocationRef.current = locationResponseFromLocationScan?.locationResponse?.locationId;
	};

	const handleLocationUpdateSuccessFromStyleScan = (response) => {
		if (!response?.isEdit) {
			updateProductLocationWithQRCode({
				sampleIds: scannedSampleIds,
				locationIdentifierNo: locationUpdateResponse?.locationId
			});
		}
	};

	const handleLocationUpdateSuccessFromLocationScan = async (response) => {
		if (!response?.isEdit && scannedItems?.length > 0) {
			await replaceLocation({
				newLocationIdentifierNo: scannedLocationRef.current,
				currentLocationId: locationResponseFromLocationScan?.locationResponse?.id
			});
		} else {
			dispatch(
				setLocationResponseFromLocationScan({
					...locationResponseFromLocationScan,
					locationResponse: {
						...response,
						isFound: true
					}
				})
			);
		}
	};

	const handleCopyToFolder = (selectedFolder, handleSuccess) => {
		likeProduct({
			productIds: selectedProductIds,
			folderId: selectedFolder?.id,
			responseType: 'SCAN'
		})
			.unwrap()
			.then(() => handleSuccess(selectedFolder));
	};

	const handleDownloadTPSummary = () => {
		downloadTpSummary({ productIds: selectedProductIds });
	};

	const handleOpenMobileCamera = () => {
		setIsScanWithCamera(true);
	};

	const handleAddToFavourite = () => {
		likeProduct({
			productIds: selectedProductIds,
			folderId: userPreference?.DEFAULT_FAVOURITE_FOLDER?.id || -1,
			responseType: 'SCAN'
		});
		setIsShowFavouriteFolders(true);
		setCurrentLikedStyle(selectedProductIds);
	};

	const handleUpdateLocationFromMoreOption = (locationInfo, remarks) => {
		dispatch(setLocationResponseFromLocationScan({}));
		updateProductLocationWithQRCode({
			sampleIds: scannedSampleIds?.filter(Boolean),
			locationIdentifierNo: locationInfo?.locationId,
			remarks: remarks
		});
		setIsShowUpdateLocationModal(false);
	};

	// Loading and Error States
	const IS_LOADING = getLoadingState([
		isFetchingProductTag,
		isDownloadTpSummaryLoading,
		isUpdatingLocation,
		isUndoingLocationUpdate,
		isSearchingLocation,
		isReplacingLocation
	]);

	const ERROR_MESSAGES = getErrorMessage([
		fetchingProductTagError,
		downloadTpSummaryError,
		undoLocationUpdateError
	]);

	// Handle error messages
	useEffect(() => {
		if (ERROR_MESSAGES?.length > 0) {
			const newErrors = ERROR_MESSAGES.filter((error) => !displayedErrors.includes(error));
			if (newErrors.length > 0) {
				newErrors.forEach((error) => toastError(error));
				setDisplayedErrors((prev) => [...prev, ...newErrors]);
			}
		}
	}, [ERROR_MESSAGES, displayedErrors]);

	const renderSearchInput = () => {
		if (isScanWithCamera) return null;

		return (
			<div className="filter-actions d-block">
				<div className="form-group size32 m-0 search-filter filter-with-animation d-flex align-items-center mw-100">
					<div className="right-icon w-100">
						<div className="search-and-dropdown-filter d-flex align-items-center gap-2">
							<InputField
								type="text"
								placeholder="Search with QR code."
								ref={searchInputRef}
								value={searchValue}
								onChangeHandler={(e) => {
									dispatch(setSearchValue(e.target.value));
									scannedLocationRef.current = e.target.value;
								}}
								withLabel={false}
								withWrapper={false}
								className="border-0"
								disabled={isSearchLoading}
								onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
							/>
							<SearchTypeDropdown
								searchType={searchType}
								setSearchType={setSearchType}
								setSearchValue={(value) => dispatch(setSearchValue(value))}
							/>
						</div>
						<span className="right-icon d-flex align-items-center"></span>
					</div>
				</div>
			</div>
		);
	};

	const renderCameraScanner = () => {
		if (!isScanWithCamera) return null;

		return (
			<>
				<ScanWithCamera
					onClose={() => setIsScanWithCamera(false)}
					onGetQrCannedResult={(value) => dispatch(setSearchValue(value))}
					setCapturedImageSrc={setCapturedImageSrc}
					ref={scanAgainRef}
				/>
				{capturedImageSrc && (
					<div className="try-again-button text-center mb-2">
						<button className="button size32" onClick={() => scanAgainRef.current?.()}>
							Scan again
						</button>
					</div>
				)}
			</>
		);
	};

	return (
		<LoaderComponent loading={IS_LOADING}>
			<div className="scan-sidebar shadow-8dp active">
				<div className="scan-sidebar-header">
					<div className="d-flex justify-content-between">
						<p className="semibold-14 reqular-14">{title}</p>
						<CommonImage src="/icons/close.svg" className="cursor-pointer" onClick={onClose} />
					</div>
				</div>
				<div className={`filter-action-content ${isScanWithCamera ? 'scan-with-camera-open' : ''}`}>
					<div className="d-flex justify-content-between align-items-center">
						<div className="select-component w-100">{renderSearchInput()}</div>
						<div className="view-type-top ml-3"></div>
					</div>
				</div>
				<div className="scanner-body scroll-y-label" ref={scanContainerRef}>
					{renderCameraScanner()}
					<ScannedStyles
						viewType={viewType}
						handleRemoveItem={handleRemoveItem}
						handleClearAll={handleClearAll}
						fetchAllTags={fetchAllTags}
						productWiseTags={productWiseTags}
						onAddTag={setSelectedSingleSample}
						onUndoLocationUpdate={handleUndoLocationUpdate}
						onSuccessLocationScanFromStyleScan={handleLocationUpdateSuccessFromStyleScan}
						handleLocationUpdateSuccessFromLocationScan={
							handleLocationUpdateSuccessFromLocationScan
						}
						isScanWithCamera={isScanWithCamera}
						onScanWithCamera={handleOpenMobileCamera}
						onSelectType={setViewType}
					/>
				</div>

				<Footer
					scanProps={{
						onScanWithCamera: handleOpenMobileCamera,
						isScanWithCamera,
						onCloseScanWithCamera: () => setIsScanWithCamera(false)
					}}
					moreActionsProps={{
						onShowMore: () => setIsShowMore(true),
						onCloseMore: () => setIsShowMore(false),
						isShowMore,
						onOpenLocationModal: () => setIsShowUpdateLocationModal(true),
						onDownloadTPSummary: handleDownloadTPSummary,
						onShowTagModal: () => setIsShowTagModal(true),
						onShowAddTaskModal: () => setIsShowTaskModal(true)
					}}
					isDisabled={scannedItems.length === 0}
					onAddToFavourite={handleAddToFavourite}
				/>

				{/* Modals */}
				{isShowFavouriteFolders && (
					<SelectFolderPopup
						onClose={() => {
							setIsShowFavouriteFolders(false);
							setCurrentLikedStyle(null);
						}}
						handleCopyToFolder={handleCopyToFolder}
						isShowCreateNew={isCreateNewFolder}
						setIsShowCreateNew={setIsCreateNewFolder}
						currentLikedStyle={currentLikedStyle}
						favFrom="SCAN"
					/>
				)}
				{selectedSingleSample && (
					<AddTagBottomPopup
						title="Add Tag"
						selectedSingleSample={selectedSingleSample}
						allTagList={productWiseTags}
						fetchAllTags={fetchAllTags}
						onClose={() => setSelectedSingleSample(null)}
						sampleId={selectedSingleSample?.id}
						setSelectedSingleSample={setSelectedSingleSample}
						updateProductTagOnSelect={updateProductTagOnSelect}
					/>
				)}
				{isShowUpdateLocationModal && (
					<UpdateSampleLocation
						selectedProductIds={selectedProductIds}
						setIsShowUpdateLocationModal={setIsShowUpdateLocationModal}
						isShowCountry={false}
						isOnTheWay={true}
						onSubmit={(selectedLocation, remarks) => {
							handleUpdateLocationFromMoreOption(selectedLocation, remarks);
						}}
					/>
				)}
				{isShowTagModal && (
					<TagUpdateModal
						type="ADD_NEW"
						title="Tag"
						onCancel={() => setIsShowTagModal(false)}
						allTagList={productWiseTags}
						commonTags={commonTags}
						updateTagList={updateProductTagOnSelect}
						fetchAllTags={fetchAllTags}
						isFavouriteProduct={true}
						favouriteProductIdList={selectedProductIds}
					/>
				)}
				{isShowTaskModal && (
					<AddTaskModal
						onClose={() => {
							// if (taskName) {
							// 	setTaskInputValueFromTaskModal(taskName);
							// }
							setIsShowTaskModal(false);
							setSelectedProductIds([]);
						}}
						// onOpenAddNewTaskModal={() => setIsShowAddNewTaskModal(true)}
						productIdList={selectedProductIds}
					/>
				)}
				{/* {isShowAddNewTaskModal && (
					<CreateNewTask
						onClose={() => {
							setIsShowAddNewTaskModal(false);
						}}
						onOpenTaskModal={() => setIsShowTaskModal(true)}
						taskInputValueFromTaskModal={taskInputValueFromTaskModal}
					/>
				)} */}
				{isShowConfirmationModal && (
					<DeleteConfirmationModal
						title="Replace Location"
						description="Are you sure about Replace location?"
						onClose={() => setIsShowConfirmationModal(false)}
						onConfirm={() => {
							handleReplaceLocation(scannedQrCode);
						}}
					/>
				)}
			</div>
		</LoaderComponent>
	);
};

export default Scan;
